import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import {
  Alert,
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import { z } from 'zod';

import { mayAdjustPrice } from 'src/features/appointments/calendarUtils';
import type { Appointment } from 'src/features/appointments/types';
import { useUpdateAppointment } from 'src/features/appointments/useAppointments';
import { useBusiness, useSession } from 'src/features/auth/useAuth';
import { useServices } from 'src/features/services/useServices';
import { fieldErrorsFromApiError } from 'src/shared/api/errors';
import { ApiError } from 'src/shared/api/types';
import AppModal from 'src/shared/components/ui/AppModal';
import { currentTimeKey, todayKey, zonedToInstant } from 'src/shared/utils/datetime';
import {
  MONEY,
  currencySymbol,
  decimalToNumber,
  formatMoney,
  toAmountInput,
} from 'src/shared/utils/money';
import { notify } from 'src/shared/utils/notify';
import rhfInput from 'src/shared/utils/rhfInput';
import zodResolver from 'src/shared/utils/zodResolver';
import {
  METHOD_LABELS,
  METHOD_OPTIONS,
  PAID_AT_REFUSED,
  canBackdate,
  extraOver,
  paidAtProblem,
  round,
  suggestedAmount,
} from './paymentUtils';
import type { PaymentMethod, PaymentPayload } from './types';
import { useRecordPayment } from './usePayments';

/** Amounts are validated as text, never with `z.coerce.number()`; see `utils/money`. */
const schema = z.object({
  amount: z.string().regex(MONEY, 'Indica un monto con hasta dos decimales'),
  method: z.string().min(1, 'Elige cómo pagó'),
  tipAmount: z.string().refine((v) => v === '' || MONEY.test(v), 'Indica una propina válida'),
  paidDate: z.string().min(1, 'Indica la fecha del cobro'),
  paidTime: z.string().regex(/^\d{2}:\d{2}/, 'Indica la hora del cobro'),
  reference: z.string(),
  notes: z.string(),
  // Only asked when the amount goes past the balance, so it is checked on
  // submit against that figure rather than here.
  extraReason: z.string().max(255, 'Usa como máximo 255 caracteres'),
});

type FormValues = z.infer<typeof schema>;

const EMPTY: FormValues = {
  amount: '',
  method: 'cash',
  tipAmount: '',
  paidDate: '',
  paidTime: '',
  reference: '',
  notes: '',
  extraReason: '',
};

const toPayload = (values: FormValues, paidAt: string): PaymentPayload => ({
  amount: decimalToNumber(values.amount),
  method: values.method as PaymentMethod,
  tipAmount: values.tipAmount ? decimalToNumber(values.tipAmount) : 0,
  paidAt,
  reference: values.reference.trim() || null,
  notes: values.notes.trim() || null,
});

/**
 * Maps a failure to something the user can act on.
 *
 * Keyed on status, never on the API's prose: it answers in English and this UI
 * is in Spanish. The 409 is "that is more than this appointment owes", which
 * the form now turns into an extra before sending — so it only arrives when
 * somebody else took money on the same appointment a moment earlier.
 */
const errorMessage = (error: Error | null): string | null => {
  if (!error) return null;
  if (!(error instanceof ApiError)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }
  if (error.status === 409) return 'Ese monto supera lo que falta por cobrar de esta cita.';
  if (error.status === 422 && error.errors[0]?.field === 'paidAt') {
    return 'Revisa la fecha del cobro.';
  }
  if (error.status === 422) return error.errors[0]?.message ?? 'Revisa los datos del formulario.';
  if (error.status === 403) return 'No tienes permiso para registrar cobros.';
  if (error.status === 404) return 'Esta cita ya no existe. Actualiza la página.';
  return 'No pudimos registrar el cobro. Inténtalo de nuevo.';
};

/** The same, for adding the extra the payment needs. Nothing was charged if this fails. */
const extraErrorMessage = (error: Error | null): string | null => {
  if (!error) return null;
  if (!(error instanceof ApiError)) {
    return 'No pudimos conectar con el servidor. Revisa tu conexión.';
  }
  if (error.status === 409) {
    return 'Alguien más cambió esta cita mientras cobrabas. Ciérrala y vuelve a abrirla.';
  }
  if (error.status === 403) return 'Solo la dueña puede sumar un adicional a una cita completada.';
  return 'No pudimos sumar el adicional, así que no se registró el cobro. Inténtalo de nuevo.';
};

const EXTRA_REFUSED =
  'Supera lo que falta por cobrar. Solo la dueña puede sumar un adicional a una cita completada.';

interface PaymentFormModalProps {
  isOpen: boolean;
  toggle: () => void;
  appointment: Appointment;
  currency?: string;
  timeZone?: string;
}

export default function PaymentFormModal({
  isOpen,
  toggle,
  appointment,
  currency,
  timeZone,
}: PaymentFormModalProps) {
  const mutation = useRecordPayment(appointment.id);
  const adjust = useUpdateAppointment();
  const { data: business } = useBusiness();
  const user = useSession();
  const mayAddExtra = mayAdjustPrice(appointment.status, user?.role);
  // Only to read the deposits the catalogue asks for. Enabled while the modal
  // is open, and served from cache the rest of the time — the services list is
  // already loaded by the booking flow.
  const { data: catalogue } = useServices({ limit: 100 });

  const depositTotal = (catalogue?.items ?? [])
    .filter(
      (service) =>
        service.requiresDeposit &&
        appointment.services.some((line) => line.serviceId === service.id)
    )
    .reduce((total, service) => total + (service.depositAmount ?? 0), 0);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    control,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY });

  // What the typed amount goes past the balance: work the client asked for on
  // top of what was booked. `useWatch`, not `watch()`, for the React Compiler.
  const typedAmount = useWatch({ control, name: 'amount' });
  const extra = MONEY.test(typedAmount)
    ? extraOver(decimalToNumber(typedAmount), appointment.balanceDue)
    : 0;

  // Reset on open, not on mount: the modal stays mounted between uses, so the
  // second payment would otherwise inherit the first one's figures.
  useEffect(() => {
    if (isOpen) {
      const suggested = suggestedAmount(appointment, depositTotal);
      reset({
        ...EMPTY,
        amount: suggested > 0 ? toAmountInput(suggested) : '',
        paidDate: todayKey(timeZone),
        paidTime: currentTimeKey(timeZone),
        extraReason: appointment.extraReason ?? '',
      });
      mutation.reset();
      adjust.reset();
    }
    // `mutation` and `adjust` are recreated per render by React Query;
    // depending on them would reset the form on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, appointment.id, depositTotal, reset]);

  const onSubmit = async (values: FormValues) => {
    mutation.reset();
    adjust.reset();
    const paidAt = zonedToInstant(values.paidDate, values.paidTime, timeZone);
    const problem = paidAtProblem(
      paidAt,
      values.paidDate,
      business?.booksClosedThrough,
      canBackdate(user?.role)
    );
    if (problem) {
      setError('paidDate', { message: problem });
      return;
    }

    // The API refuses a payment above the balance, so the extra goes onto the
    // appointment first. If that fails nothing is charged; if the payment
    // fails afterwards the extra stays, the balance already shows it, and a
    // retry is an ordinary payment.
    if (extra > 0) {
      if (!mayAddExtra) {
        setError('amount', { message: EXTRA_REFUSED });
        return;
      }
      const reason = values.extraReason.trim();
      if (!reason) {
        setError('extraReason', { message: 'Cuenta qué se hizo de más' });
        return;
      }
      try {
        await adjust.mutateAsync({
          id: appointment.id,
          data: {
            extraAmount: round(appointment.extraAmount + extra),
            extraReason: reason,
            version: appointment.version,
          },
        });
      } catch {
        return; // `adjust.error` says why, above the form
      }
    }

    mutation.mutate(toPayload(values, paidAt), {
      onSuccess: (payment) => {
        notify.success(`Cobro registrado · ${formatMoney(payment.amount, currency)}`);
        toggle();
      },
      onError: (error: Error) => {
        for (const [field, message] of Object.entries(fieldErrorsFromApiError(error))) {
          if (field === 'paidAt') setError('paidDate', { message: PAID_AT_REFUSED });
          else if (field in EMPTY) setError(field as keyof FormValues, { message });
        }
      },
    });
  };

  const message = extraErrorMessage(adjust.error) ?? errorMessage(mutation.error);
  const isPending = adjust.isPending || mutation.isPending;

  return (
    <AppModal
      isOpen={isOpen}
      toggle={toggle}
      backdrop="static"
      title="Registrar cobro"
      onSubmit={(e) => void handleSubmit(onSubmit)(e)}
      footer={
        <>
          <Button color="link" onClick={toggle} type="button">
            Cancelar
          </Button>
          <Button color="primary" type="submit" disabled={isPending}>
            {isPending && <Spinner size="sm" className="me-2" />}
            Registrar cobro
          </Button>
        </>
      }
    >
      {message && <Alert color="danger">{message}</Alert>}

      <p className="text-muted small">
        Falta por cobrar: <strong>{formatMoney(appointment.balanceDue, currency)}</strong>
      </p>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label for="paymentAmount">Monto *</Label>
            <InputGroup>
              <InputGroupText>{currencySymbol(business?.currency)}</InputGroupText>
              <Input
                id="paymentAmount"
                inputMode="decimal"
                autoComplete="off"
                placeholder="20.00"
                invalid={Boolean(errors.amount)}
                {...rhfInput(register('amount'))}
              />
              {errors.amount && <FormFeedback>{errors.amount.message}</FormFeedback>}
            </InputGroup>
            {extra > 0 && !mayAddExtra && !errors.amount && (
              <small className="text-warning">{EXTRA_REFUSED}</small>
            )}
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="paymentMethod">Forma de pago *</Label>
            <Input
              id="paymentMethod"
              type="select"
              invalid={Boolean(errors.method)}
              {...rhfInput(register('method'))}
            >
              {METHOD_OPTIONS.map((method) => (
                <option key={method} value={method}>
                  {METHOD_LABELS[method]}
                </option>
              ))}
            </Input>
            {errors.method && <FormFeedback>{errors.method.message}</FormFeedback>}
          </FormGroup>
        </Col>
      </Row>

      {extra > 0 && mayAddExtra && (
        <FormGroup>
          <Label for="paymentExtraReason">
            Adicional de {formatMoney(extra, currency)}: ¿qué se hizo de más? *
          </Label>
          <Input
            id="paymentExtraReason"
            autoComplete="off"
            placeholder="Ej.: reparación de una uña"
            invalid={Boolean(errors.extraReason)}
            {...rhfInput(register('extraReason'))}
          />
          {errors.extraReason && <FormFeedback>{errors.extraReason.message}</FormFeedback>}
          <small className="text-muted">
            Supera lo que falta por cobrar, así que se suma a la cita: el total pasa a{' '}
            {formatMoney(round(appointment.totalAmount + extra), currency)}.
          </small>
        </FormGroup>
      )}

      <Row>
        <Col xs={7} md={6}>
          <FormGroup>
            <Label for="paymentDate">Fecha *</Label>
            <Input
              id="paymentDate"
              type="date"
              invalid={Boolean(errors.paidDate)}
              {...rhfInput(register('paidDate'))}
            />
            {errors.paidDate && <FormFeedback>{errors.paidDate.message}</FormFeedback>}
          </FormGroup>
        </Col>
        <Col xs={5} md={6}>
          <FormGroup>
            <Label for="paymentTime">Hora *</Label>
            <Input
              id="paymentTime"
              type="time"
              invalid={Boolean(errors.paidTime)}
              {...rhfInput(register('paidTime'))}
            />
            {errors.paidTime && <FormFeedback>{errors.paidTime.message}</FormFeedback>}
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col md={6}>
          <FormGroup>
            <Label for="paymentTip">Propina</Label>
            <InputGroup>
              <InputGroupText>{currencySymbol(business?.currency)}</InputGroupText>
              <Input
                id="paymentTip"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0.00"
                invalid={Boolean(errors.tipAmount)}
                {...rhfInput(register('tipAmount'))}
              />
              {errors.tipAmount && <FormFeedback>{errors.tipAmount.message}</FormFeedback>}
            </InputGroup>
            <small className="text-muted">No se descuenta del total de la cita.</small>
          </FormGroup>
        </Col>
        <Col md={6}>
          <FormGroup>
            <Label for="paymentReference">Referencia</Label>
            <Input
              id="paymentReference"
              autoComplete="off"
              placeholder="Nº de transferencia o voucher"
              {...rhfInput(register('reference'))}
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for="paymentNotes">Nota</Label>
        <Input id="paymentNotes" type="textarea" rows={2} {...rhfInput(register('notes'))} />
      </FormGroup>
    </AppModal>
  );
}
